import { Component, input, output, OnDestroy, OnInit, signal } from '@angular/core';
import { concat, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { HttpService } from 'src/app/services/http.service';
import { ControlPoint } from '../../bo/controlPoint';
import { EPSG } from '../../bo/epsg';
import { Site } from '../../bo/site';
import { Coordinate } from 'src/app/bo/coordinate';
import { CoordinateTransform } from 'src/app/bo/coordinateTransform';


@Component({
  selector: 'app-epsg-modale',
  templateUrl: './epsg-modale.component.html',
  styleUrls: ['./epsg-modale.component.scss'],
  standalone: false
})
export class EpsgModaleComponent implements OnInit, OnDestroy {
  epsgSite = output<string>();
  epsgCoordinates = output<CoordinateTransform>();
  epsgModif = output<boolean>();
  // epsgCoordinatesModif = output<boolean>();
  coordX = output<any>();
  coordY = output<any>();
  coordZ = output<any>();
  invalide = output<any>();

  mySite = input<Site>();
  isSite = input<boolean>();
  myControlPoint = input<ControlPoint>();
  newTransformCoordinate = input<Coordinate>();

  /**
   * Signals : ces champs sont écrits depuis des callbacks HTTP. L'application tourne en
   * `provideZonelessChangeDetection()`, où une réponse HttpClient ne planifie aucune détection
   * de changement — l'écriture d'un signal, si. En champs simples, les selects gardaient leur
   * placeholder alors que la valeur était bien arrivée.
   */
  readonly epsgSelectionneSite = signal<EPSG | null>(null);
  readonly epsgSelectionneCoordinates = signal<EPSG | null>(null);
  readonly epsgSelectionneCoordinatesControlPoint = signal<EPSG | null>(null);
  /** Jamais lu par le template : pas besoin d'en faire un signal. */
  lastEpsgCoordinates: EPSG;

  readonly xAffiche = signal<number | string | null>(null);
  readonly yAffiche = signal<number | string | null>(null);
  readonly zAffiche = signal<number | string | null>(null);

  input$ = new Subject<string>();
  tabEPSG$: Observable<EPSG[]>;
  epsgLoading = false;
  unsubscribe$ = new Subject<void>();
  coordXInvalide = false;
  coordYInvalide = false;
  coordZInvalide = false;
  readonly libelleX = signal("EPSG.X");
  readonly libelleY = signal("EPSG.Y");
  readonly uniteExiste = signal(false);
  
  private readonly url: string = "Sites/checkEPSG/";
  
  constructor(private readonly http: HttpService) { }

  ngOnChanges(){
   if(this.newTransformCoordinate() != undefined && !this.epsgSelectionneCoordinates()?.value){
    this.http.getWithParams<EPSG[]>(this.url, {epsg: this.epsgSelectionneCoordinates()?.value})
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (result) => {
        this.tradUnite(this.lastEpsgCoordinates);
        this.coordY.emit(this.yAffiche());
        this.coordX.emit(this.xAffiche());
      });

    this.xAffiche.set(this.newTransformCoordinate().coordX);
    this.yAffiche.set(this.newTransformCoordinate().coordY);
   }
  }


  ngOnInit(): void {
    if (this.mySite().epsg != null) {
      this.http.getWithParams<EPSG[]>(this.url, { epsg : this.mySite().epsg })
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (result) => {
            this.epsgSelectionneSite.set(result[0]);
            this.epsgSelectionneCoordinatesControlPoint.set(result[0]);
            // Les X/Y affichés sont ceux du site, donc exprimés dans l'EPSG du site : c'est
            // aussi ce que le back retient par défaut à l'enregistrement (SitesService :
            // EPSGCoordinates ?? EPSG). Sans cette présélection le select restait vide même
            // une fois la détection de changement réparée, l'API ne renvoyant jamais
            // epsgCoordinates — c'est un champ d'entrée, pas une donnée persistée.
            this.epsgSelectionneCoordinates.set(result[0]);
            this.lastEpsgCoordinates = result[0];
            this.tradUnite(this.epsgSelectionneSite());
          });
      if (this.isSite()) {
        this.xAffiche.set(this.mySite().coordX);
        this.yAffiche.set(this.mySite().coordY);
      } else if (this.myControlPoint() != null) {
        this.xAffiche.set(this.myControlPoint().x);
        this.yAffiche.set(this.myControlPoint().y);
        this.zAffiche.set(this.myControlPoint().z);
      }
    }

    this.loadEPSG();
  }

  tradUnite(epsg: EPSG) {
    if (epsg) {
      if (epsg.unite == "") {
        this.uniteExiste.set(false);
        this.libelleX.set("EPSG.X");
        this.libelleY.set("EPSG.Y");
      }
      else if (epsg.unite.includes("metre")) {
        this.uniteExiste.set(true);
        epsg.unite = "EPSG.METRE";
        this.libelleX.set("EPSG.X");
        this.libelleY.set("EPSG.Y");
      }
      else if (epsg.unite.includes("degree")) {
        this.uniteExiste.set(true);
        epsg.unite = "EPSG.DEGRE";
        this.libelleX.set("EPSG.LONG");
        this.libelleY.set("EPSG.LAT");
      }
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  loadEPSG() {
    this.tabEPSG$ = concat(
      of([]),
      this.input$.pipe(
        distinctUntilChanged(),
        tap(() => this.epsgLoading = true),
        switchMap(term => {
          return this.checkEPSG(term).pipe(
            catchError(() => of([])),
            tap(() => {
              this.epsgLoading = false;
              this.tradUnite(this.epsgSelectionneSite());
            })
          )
        })
      )
    );
  }

  checkEPSG(epsg: string): Observable<EPSG[]> {
    if(!epsg || epsg.trim() === '') {
      console.error("EPSG code is required.");
      return of([]);
    }
    return this.http.getWithParams<EPSG[]>(this.url, {epsg: epsg})
      .pipe(takeUntil(this.unsubscribe$))
      .pipe(map(result => {
        return result;
      },
        (error) => {
          console.log(error);
        }
      ))
  }

  onSelection() {
    const epsgSite = this.epsgSelectionneSite();
    if (epsgSite != null) {
      // this.tradUnite();
      // if (this.xAffiche != null) this.onUpdateX(this.xAffiche);
      // if (this.yAffiche != null) this.onUpdateY(this.yAffiche);
      // if (!this.isSite && this.zAffiche != null) this.onUpdateZ(this.zAffiche);
      this.epsgSite.emit(epsgSite.value);
      this.epsgModif.emit(true);
    }
  }
  onSelectionCoordinatesControlPoint() {
    const epsgControlPoint = this.epsgSelectionneCoordinatesControlPoint();
    if (epsgControlPoint != null) {
      // this.tradUnite();
      // if (this.xAffiche != null) this.onUpdateX(this.xAffiche);
      // if (this.yAffiche != null) this.onUpdateY(this.yAffiche);
      // if (!this.isSite && this.zAffiche != null) this.onUpdateZ(this.zAffiche);
      this.epsgSite.emit(epsgControlPoint.value);
      this.epsgModif.emit(true);

      this.tradUnite(epsgControlPoint);

      if (this.xAffiche() != null) this.onUpdateX(this.xAffiche());
      if (this.yAffiche() != null) this.onUpdateY(this.yAffiche());
      if (!this.isSite() && this.zAffiche() != null) this.onUpdateZ(this.zAffiche());

      const epsgTransform = new CoordinateTransform();
      if(this.lastEpsgCoordinates != undefined) epsgTransform.EPSGin = parseInt(this.lastEpsgCoordinates.value);
      epsgTransform.EPSGout = parseInt(epsgControlPoint.value);

      this.epsgCoordinates.emit(epsgTransform);

      this.lastEpsgCoordinates = epsgControlPoint;

    }
  }
  onSelectionEpsgCoordinates() {
    const epsgCoordinates = this.epsgSelectionneCoordinates();
    if (epsgCoordinates != null) {

      this.tradUnite(this.epsgSelectionneSite());

      if (this.xAffiche() != null) this.onUpdateX(this.xAffiche());
      if (this.yAffiche() != null) this.onUpdateY(this.yAffiche());
      if (!this.isSite() && this.zAffiche() != null) this.onUpdateZ(this.zAffiche());

      const epsgTransform = new CoordinateTransform();
      if(this.lastEpsgCoordinates != undefined) epsgTransform.EPSGin = parseInt(this.lastEpsgCoordinates.value);
      epsgTransform.EPSGout = parseInt(epsgCoordinates.value);

      this.epsgCoordinates.emit(epsgTransform);

      this.lastEpsgCoordinates = epsgCoordinates;
    }
  }

  onUpdateX(e) {
    var charTest = (/^[0-9.,-]*$/).test(e);
    if (charTest) this.coordXInvalide = false;
    else this.coordXInvalide = true;
    this.coordX.emit(e);
    this.invalide.emit(this.coordXInvalide || this.coordYInvalide || this.coordZInvalide);
  }

  onUpdateY(e) {
    var charTest = (/^[0-9.,-]*$/).test(e);
    if (charTest) this.coordYInvalide = false;
    else this.coordYInvalide = true;
    this.coordY.emit(e);
    this.invalide.emit(this.coordXInvalide || this.coordYInvalide || this.coordZInvalide);
  }

  onUpdateZ(e) {
    var charTest = (/^[0-9.,-]*$/).test(e);
    if (charTest) this.coordZInvalide = false;
    else this.coordZInvalide = true;
    this.coordZ.emit(e);
    this.invalide.emit(this.coordXInvalide || this.coordYInvalide || this.coordZInvalide);
  }
}
